import { uniqBy } from 'lodash-es';

import type { TrackingFn } from '@change-corgi/core-react/tracking';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PetitionDraft } from '#src/app/shared/api/petitionDraft';

import { InferDecisionMakers } from './api.graphql';
import type { InferDecisionMakersMutation, InferDecisionMakersMutationVariables } from './api.graphql';
import { eligibleDms } from './utils';

type Input = {
	title: string;
	description?: string;
	/**
	 * The inferred target's own location — `adminArea`/`countryCode` are real ISO codes per the
	 * scope/location inference prompt (e.g. `"IL"`, `"US"`), not full names. `stateCode` on the
	 * mutation's own input is renamed from `adminArea` here so callers don't need to know that.
	 */
	location: { city: string | null; adminArea: string | null; countryCode: string | null };
	confidenceThreshold?: number;
	maxTargets?: number;
	/** Not sent to petition_service -- only used by the middle-end to key its full-response log. */
	draftId?: string;
};

type DraftDecisionMakers = NonNullable<NonNullable<PetitionDraft['metadata']>['decisionMakers']>;
/** A decision maker as the draft keeps it, so what this returns is what `applyDecisionMakers` stores. */
type DecisionMaker = NonNullable<DraftDecisionMakers['recommendations']>[number];

/** The kept decision makers plus the run's classification, which publish stores on the petition. */
export type InferDecisionMakersOutput = {
	decisionMakers: DecisionMaker[];
	classification: DraftDecisionMakers['classification'];
};

/** Full success payload (CHANGE-98274), used for tracking. */
export type InferDecisionMakersResult = Extract<
	InferDecisionMakersMutation['inferDecisionMakers'],
	{ __typename: 'InferDecisionMakersSuccess' }
>;

/**
 * Thin wrapper over the middle-end `inferDecisionMakers` mutation, tracking its own success and
 * error the same way `inferPetitionScopeLocation`'s `api.ts` does. Returns `null` when there's no
 * title or no resolved `countryCode` to infer from (no call, no tracking). Eligibility (local scope,
 * resolved city and admin area, supported target country) is decided before this is called, by the
 * variant actor's `requestDecisionMakers`; this is just the last-mile guard against a missing target. Throws on the typed-error arm and on
 * transport failure so the caller can discriminate success vs. failure; callers that treat this as
 * best-effort (the SAP background call) are responsible for swallowing the rejection.
 */
export async function inferDecisionMakers(
	input: Input,
	{
		utilityContext: {
			gql: { fetch },
		},
		track,
	}: { utilityContext: UtilityContext; track: TrackingFn },
): Promise<InferDecisionMakersOutput | null> {
	if (!input.title || !input.location.countryCode) return null;

	void track('sap_dmi_triggered');

	let response: InferDecisionMakersMutation['inferDecisionMakers'];
	try {
		const { data } = await fetch<InferDecisionMakersMutation, InferDecisionMakersMutationVariables>({
			query: InferDecisionMakers,
			// Matches the mutation's @restrictPath directive; required for the call to resolve.
			path: '/infer-decision-makers',
			variables: {
				input: {
					title: input.title,
					description: input.description,
					confidenceThreshold: input.confidenceThreshold,
					maxTargets: input.maxTargets,
					draftId: input.draftId,
					relevantLocation: {
						countryCode: input.location.countryCode,
						city: input.location.city ?? undefined,
						stateCode: input.location.adminArea ?? undefined,
					},
				},
			},
			rejectOnError: true,
		});
		response = data.inferDecisionMakers;
	} catch (e: unknown) {
		void track('sap_dmi_error', { dmi_error_reason: 'TRANSPORT_ERROR' });
		throw e;
	}

	// `error` is the generated error-reason enum, tracked directly here (rather than round-tripped
	// through a thrown Error's message) so a new server value is a type error against
	// `dmi_error_reason`, not a silent collapse to TRANSPORT_ERROR.
	if (response.__typename !== 'InferDecisionMakersSuccess') {
		void track('sap_dmi_error', { dmi_error_reason: response.error });
		throw new Error(response.error);
	}

	// callers only get the ones the service would assign and that can be attached at publish (a decision maker
	// without a petition target id can't be), once per petition target (two politicians can share one), with just
	// the fields the flow keeps; tracking reports the full set alongside their count
	const decisionMakers = uniqBy(
		response.recommendations.flatMap(({ wouldAssign, canonicalPetitionTargetId, name, position, reasoning }) =>
			wouldAssign && canonicalPetitionTargetId ? [{ canonicalPetitionTargetId, name, position, reasoning }] : [],
		),
		({ canonicalPetitionTargetId }) => canonicalPetitionTargetId,
	);

	void track('sap_dmi_completed', {
		dm_count: decisionMakers.length,
		dmi_status: response.status,
		dmi_confidence_threshold: response.effectiveParams.confidenceThreshold,
		dmi_confidence_threshold_source: response.effectiveParams.confidenceThresholdSource,
		dmi_max_targets: response.effectiveParams.maxTargets ?? undefined,
		dmi_max_targets_source: response.effectiveParams.maxTargetsSource,
		dmi_top_recommendations: response.topRecommendations.map(String),
		eligible_dms: eligibleDms(response),
	});

	// a JSON scalar; petition_service always sends an object
	return { decisionMakers, classification: (response.classification as Record<string, unknown> | null) ?? null };
}
